import React from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import {
  addWidgetComponents,
  selectConversationId,
  selectForethoughtLiveChatWidgetComponent,
} from 'reducers/workflows';
import type {
  KustomerConversationResponse,
  KustomerGetMessagesResponse,
  KustomerInitAssistantResponse,
  KustomerListenerEventCallbackResponse,
  KustomerSettings,
} from 'embed/kustomerCore';
import {
  applyKustomerCoreFunctionWithCallback,
  KUSTOMER_CORE_SDK,
  KUSTOMER_SCRIPT_ID,
  sendKustomerMessage,
} from 'embed/kustomerCore';
import type {
  ButtonComponent,
  KustomerLiveChatIntegrationConfig,
  KustomerOneChatOnLoadConfig,
  UserMessageComponent,
  WidgetComponent,
} from 'api/types';
import {
  selectChatId,
  setAgentInfo,
  setIsInAssistantMode,
  setLiveChatIdAndStatus,
  setLiveChatStatus,
} from 'reducers/live-chat';
import {
  addLiveChatEventMessage,
  addLiveChatWidgetComponents,
  handleAgentTyping,
  updateForethoughtLiveChatHelpdeskConversationId,
} from 'actions/live-chat';
import { debounce } from 'utils/debounce';
import { useLiveChatFailure } from './useLiveChatFailure';
import { updateHelpdeskConversation } from 'actions/workflows-v2/workflowsV2';
import { sendTrackingEvent } from 'actions/workflows/workflows';
import { getExceptionMessage, retryFunction } from 'embed/utils';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';
import { selectOnLoadKustomerJwt } from 'reducers/config';

/**
 * In all my testing, the most reliable way to get duplicated chats
 * was to enable Tailscale exit node `west-2b`. And no matter what i did
 * to mess with network speeds or browser tab manipulation, 2500 always worked.
 */
const TIMEOUT_DURATION = 2500;
const TEN_MINUTES = 1000 * 60 * 10;
let presenceInterval: NodeJS.Timeout | undefined;

const unescapeLinks = (text: string): string => {
  const escapedCharactersRegex = /\\(.)/g;
  const urlRegex = /https?:\/\/[^\s/$.?#].[^\s]*/g;

  return text.replace(urlRegex, match => {
    return match.replace(escapedCharactersRegex, '$1');
  });
};

const addKustomerSdkScript = ({
  apiKey,
  onLoad,
}: {
  apiKey: string;
  onLoad: () => Promise<void>;
}): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = KUSTOMER_SCRIPT_ID;
    script.src = KUSTOMER_CORE_SDK;
    script.setAttribute('data-kustomer-api-key', apiKey);
    script.fetchPriority = 'high';
    script.onload = async () => {
      try {
        await onLoad();
        resolve();
      } catch (err) {
        // if onLoad fails, throw that error
        reject(err);
      }
    };
    script.onerror = () => {
      reject(new Error('Failed to load Kustomer SDK script'));
    };
    document.body.appendChild(script);
  });
};

interface InitializeKustomerSdk {
  brandId: string;
  email: string | null;
  forceInit?: boolean;
  jwt: string | null;
  kustomerConversationId: string;
  onLoginFailure: (err: unknown) => void;
  shouldLogout?: boolean;
  skipAvailabilityCheck?: boolean;
}

const isCustomerLoggedIn = async (email: string | null) => {
  if (!email) {
    return false;
  }

  try {
    return await applyKustomerCoreFunctionWithCallback('isLoggedIn', { email });
  } catch (err) {
    // do nothing isLoggedIn is not even in the docs
    return false;
  }
};

const getMessages = async (kustomerConversationId: string) => {
  return await applyKustomerCoreFunctionWithCallback('getMessages', {
    conversationId: kustomerConversationId,
  });
};

const sendPresenceActivity = () => {
  try {
    window.KustomerCore?.sendPresenceActivity?.({ presence: 'online' });
  } catch (err) {
    sendErrorToSentry({
      consoleError: true,
      error: `Failed to send presence activity -- ${getExceptionMessage(err)}`,
    });
  }
};

const initializeKustomerSdk = async ({
  brandId,
  email,
  forceInit = false,
  jwt,
  kustomerConversationId,
  onLoginFailure,
  shouldLogout = false,
  skipAvailabilityCheck = false,
}: InitializeKustomerSdk) => {
  let chatSettings = window.KustomerCore?.getChatSettings?.();
  let messages: KustomerGetMessagesResponse | undefined;

  /**
   * This is the only API I have found that throws specific pubnub
   * errors that prevent listeners from working. The SDK was
   * initialized, messages could be sent, adding listeners wouldn't throw errors.
   * If we can't get the messages, we need to call init again.
   *
   * The error was "Error: PubNub isn't connected"
   */
  try {
    if (kustomerConversationId) {
      messages = await getMessages(kustomerConversationId);
    }
  } catch (err) {
    // do nothing
  }

  if (
    chatSettings?.brandId !== brandId ||
    (kustomerConversationId && !messages) ||
    forceInit
  ) {
    chatSettings = (
      await applyKustomerCoreFunctionWithCallback('init', {
        brandId,
      })
    ).settings;
  }

  // Login
  if (jwt && !(await isCustomerLoggedIn(email))) {
    try {
      if (shouldLogout) {
        try {
          await applyKustomerCoreFunctionWithCallback('logout');
        } catch {
          // do nothing, not worried about logout failing
        }
      }

      await applyKustomerCoreFunctionWithCallback('login', {
        jwtToken: jwt,
      });
    } catch (err) {
      onLoginFailure(err);
    }
  }

  // Availability check
  if (!kustomerConversationId && !skipAvailabilityCheck) {
    const availability = window.KustomerCore?.isChatAvailable?.().availability;
    if (availability !== 'online') {
      throw new Error(`Chat availability is ${availability}`);
    }
  }

  /**
   * (4/14/2025) Met with Kustomer's dev and they recommended
   * calling `sendPresenceActivity` after login in order to avoid
   * `PNAccessDeniedCategory` errors as well as listener issues. We
   * will call this every 10 minutes to keep the session alive, PubNub
   * session supposedly lasts 60 minutes.
   *
   * (7/25/2025) Still happening so we are trying to be more aggressive
   * with sending presence activity.
   */
  sendPresenceActivity();
  clearInterval(presenceInterval);
  presenceInterval = setInterval(() => {
    sendPresenceActivity();
  }, TEN_MINUTES);

  return chatSettings;
};

/**
 * (2/11/25) Elementor wanted retries, however my testing shows that retrying doesn't
 * seem to help if there is any errors with initializing. Will keep for now but lowering
 * to only 3 retries.
 */
const retryInitializeKustomerSdk = async ({
  brandId,
  email,
  jwt,
  kustomerConversationId,
  onLoginFailure,
  shouldLogout,
  skipAvailabilityCheck,
}: InitializeKustomerSdk) => {
  return await retryFunction<KustomerSettings>({
    callback: async () => {
      return await initializeKustomerSdk({
        brandId,
        email,
        jwt,
        kustomerConversationId,
        onLoginFailure,
        shouldLogout,
        skipAvailabilityCheck,
      });
    },
    initialDelayMs: 0,
    intervalMs: 1000,
    numRetries: 3,
  });
};

export const initializeKustomerSdkOnWidgetLoad = async ({
  config,
  onLoginFailure,
}: {
  config: KustomerOneChatOnLoadConfig;
  onLoginFailure: (err: unknown) => void;
}) => {
  try {
    await addKustomerSdkScript({
      apiKey: config.key,
      onLoad: async () => {
        try {
          await retryInitializeKustomerSdk({
            brandId: config.brand_id,
            email: null,
            jwt: config.jwt,
            kustomerConversationId: '', // must be empty string
            onLoginFailure,
            shouldLogout: true,
            skipAvailabilityCheck: true,
          });
        } catch {
          /**
           * do nothing when it fails on page load,
           * we track the errors during handoff
           */
        }
      },
    });
  } catch {
    /**
     * do nothing when it fails on page load,
     * we track the errors during handoff
     */
  }
};

/**
 * (4/3/2025) Adding retry to this function because Kustomer's dev
 * said that PNAccessDeniedCategory error may be due to a race condition
 * even though we are awaiting every interaction with the Kustomer Core SDK.
 */
const createKustomerConversation = async ({
  integrationFields,
}: {
  integrationFields: KustomerLiveChatIntegrationConfig['integration_fields'];
}) => {
  const title = integrationFields.visitor_question
    ? integrationFields.visitor_question
    : 'Forethought Widget Handoff';

  return await retryFunction({
    callback: async () => {
      return await applyKustomerCoreFunctionWithCallback('createConversation', {
        brand: integrationFields.brand_id,
        custom: integrationFields.custom_fields,
        title: title.substring(0, 255),
      });
    },
    initialDelayMs: 0,
    intervalMs: 1000,
    numRetries: 3,
  });
};

const updateKustomerCustomer = async (email: string) => {
  return await applyKustomerCoreFunctionWithCallback('describeCustomer', {
    attributes: {
      sharedEmails: [email],
    },
  });
};

const getAvatarUrl = (url: string) => {
  if (!url || url.endsWith('blank')) {
    return '';
  }
  return url;
};

export const useHandleKustomerChatInitialization = () => {
  const dispatch = useAppDispatch();
  const handleLiveChatFailure = useLiveChatFailure();
  const conversationId = useAppSelector(selectConversationId);
  const liveChatWidgetComponent = useAppSelector(
    selectForethoughtLiveChatWidgetComponent,
  );
  const isCurrentTabPrimary = useAppSelector(selectIsCurrentTabPrimary);
  const chatId = useAppSelector(selectChatId);
  const onLoadJwt = useAppSelector(selectOnLoadKustomerJwt);

  /**
   * Debounce is needed here so that `getMessages` and
   * passing all messages through `onMessageReceived` doesn't
   * cause agent icon to flash.
   */
  const debounceSetAgentInfo = React.useMemo(
    () =>
      debounce(
        (
          response: KustomerListenerEventCallbackResponse<'onMessageReceived'>,
        ) => {
          if (response) {
            dispatch(
              setAgentInfo({
                agentIcon: getAvatarUrl(response.sentBy.avatarUrl),
                agentName: response.sentBy.displayName,
              }),
            );
          }
        },
        300,
      ),
    [dispatch],
  );

  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreaddlistener#onmessagereceived
   */
  const onMessageReceived = React.useCallback(
    (
      response: KustomerListenerEventCallbackResponse<'onMessageReceived'>,
      _?: Error,
    ) => {
      // only handle agent messages
      if (response && response.direction !== 'in') {
        let widgetComponents: WidgetComponent[];
        const message = unescapeLinks(response.body);

        if (response.meta.template?.templateType === 'quick_replies') {
          widgetComponents = [
            {
              component_fields: {
                agentIcon: getAvatarUrl(response.sentBy.avatarUrl),
                agentName: response.sentBy.displayName,
                author: response.sentBy.displayName,
                multi_select: false,
                options: response.meta.template.actions.map(option => ({
                  display_name: option.displayText,
                  value: option.value,
                })),
                output_variable: '',
                prompt: message,
                should_render_as_markdown: true,
                should_render_single_new_line_characters: true,
              },
              component_type: 'buttons',
              hideTimestamp: true,
              isInAgentConvo: true,
              timestamp: Date.parse(response.createdAt),
            } satisfies ButtonComponent,
          ];
        } else {
          // handle text message
          widgetComponents = [
            {
              component_fields: {
                agentIcon: getAvatarUrl(response.sentBy.avatarUrl),
                agentName: response.sentBy.displayName,
                author: response.sentBy.displayName,
                message: message,
                should_render_as_markdown: true,
                should_render_single_new_line_characters: true,
              },
              component_type: 'text_message',
              hideTimestamp: true,
              isInAgentConvo: true,
              isSent: true,
              timestamp: Date.parse(response.createdAt),
            } satisfies UserMessageComponent,
          ];
        }

        // handle attachments
        if (response.attachments && response.attachments.length) {
          response.attachments.forEach(attachment => {
            widgetComponents.push({
              component_fields: {
                agentIcon: getAvatarUrl(response.sentBy.avatarUrl),
                agentName: response.sentBy.displayName,
                attachments: [
                  {
                    content_type: attachment.contentType,
                    file_extension: attachment.contentType.split('/').pop(),
                    filesize: attachment.contentLength,
                    name: attachment.name,
                    url: attachment.url,
                  },
                ],
                author: response.sentBy.displayName,
                message: '',
              },
              component_type: 'text_message',
              hideTimestamp: true,
              isInAgentConvo: true,
              isSent: true,
              timestamp: Date.parse(response.createdAt),
            } satisfies UserMessageComponent);
          });
        }

        debounceSetAgentInfo(response);
        dispatch(
          addLiveChatWidgetComponents({
            components: widgetComponents,
            helpdeskConversationId: response.conversationId,
            sendAgentMessage: true,
          }),
        );
        // TODO: handle articles, and templates
      }
    },
    [debounceSetAgentInfo, dispatch],
  );

  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreaddlistener#onconversationcreate
   */
  const onConversationCreate = React.useCallback(
    async (
      response: KustomerListenerEventCallbackResponse<'onConversationCreate'>,
      _?: Error,
    ) => {
      if (response) {
        dispatch(
          setLiveChatIdAndStatus({
            chatId: response.conversationId,
            status: 'pending',
          }),
        );
        try {
          await dispatch(
            updateForethoughtLiveChatHelpdeskConversationId(
              response.conversationId,
            ),
          ).unwrap();
        } catch (err) {
          sendErrorToSentry({
            consoleError: true,
            conversationId,
            error: `onConversationCreate -- ${getExceptionMessage(err)}`,
          });
        } finally {
          dispatch(setLiveChatStatus('active'));
        }
      }
    },
    [conversationId, dispatch],
  );

  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreaddlistener#onconversationended
   */
  const onConversationEnded = React.useCallback(
    (
      response: KustomerListenerEventCallbackResponse<'onConversationEnded'>,
      _?: Error,
    ) => {
      if (response && response.conversationId === chatId) {
        dispatch(addLiveChatEventMessage({ message: 'Conversation ended' }));
        dispatch(setLiveChatStatus('inactive'));
      }
    },
    [chatId, dispatch],
  );

  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreaddlistener#onconversationunended
   */
  const onConversationUnended = React.useCallback(
    (
      response: KustomerListenerEventCallbackResponse<'onConversationUnended'>,
      _?: Error,
    ) => {
      if (response && response.conversationId === chatId) {
        dispatch(addLiveChatEventMessage({ message: 'Conversation reopened' }));
        dispatch(setLiveChatStatus('active'));
      }
    },
    [chatId, dispatch],
  );

  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreaddlistener#onagenttypingactivity
   */
  const onAgentTypingActivity = React.useCallback(
    (
      response: KustomerListenerEventCallbackResponse<'onAgentTypingActivity'>,
      _?: Error,
    ) => {
      if (response && response.conversationId === chatId) {
        dispatch(handleAgentTyping({ isTyping: response.typing }));
      }
    },
    [chatId, dispatch],
  );

  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreaddlistener#onassistantended
   */
  const onAssistantEnded = React.useCallback(
    (
      response: KustomerListenerEventCallbackResponse<'onAssistantEnded'>,
      _?: Error,
    ) => {
      if (response && response.conversationId === chatId) {
        dispatch(setIsInAssistantMode(response.isInAssistantMode));
      }
    },
    [chatId, dispatch],
  );

  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreaddlistener#onsatisfactionreceived
   */
  const onSatisfactionReceived = React.useCallback(
    (
      response: KustomerListenerEventCallbackResponse<'onSatisfactionReceived'>,
      _?: Error,
    ) => {
      if (response && response.conversationId === chatId) {
        /**
         * Use addWidgetComponents to not mess with liveChatStatus
         */
        dispatch(
          addWidgetComponents([
            {
              component_fields: response,
              component_type: 'kustomer_satisfaction_survey',
            },
          ]),
        );
      }
    },
    [chatId, dispatch],
  );

  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreremovelistener
   */
  const removeListeners = React.useCallback(() => {
    if (
      !window.KustomerCore ||
      typeof window.KustomerCore.removeListener !== 'function'
    ) {
      return;
    }

    try {
      window.KustomerCore.removeListener(
        'onMessageReceived',
        onMessageReceived,
      );
      window.KustomerCore.removeListener(
        'onConversationCreate',
        onConversationCreate,
      );
      window.KustomerCore.removeListener(
        'onConversationEnded',
        onConversationEnded,
      );
      window.KustomerCore.removeListener(
        'onConversationUnended',
        onConversationUnended,
      );
      window.KustomerCore.removeListener(
        'onAgentTypingActivity',
        onAgentTypingActivity,
      );
      window.KustomerCore.removeListener('onAssistantEnded', onAssistantEnded);
      window.KustomerCore.removeListener(
        'onSatisfactionReceived',
        onSatisfactionReceived,
      );
    } catch (err) {
      sendErrorToSentry({
        consoleError: true,
        conversationId,
        error: `removeListeners -- ${getExceptionMessage(err)}`,
      });
    }
  }, [
    conversationId,
    onAgentTypingActivity,
    onAssistantEnded,
    onConversationCreate,
    onConversationEnded,
    onConversationUnended,
    onMessageReceived,
    onSatisfactionReceived,
  ]);

  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreaddlistener
   */
  const setupListeners = React.useCallback(() => {
    if (!window.KustomerCore) {
      sendErrorToSentry({
        consoleError: true,
        conversationId,
        error: 'setupListeners -- window.KustomerCore not defined',
      });
      return;
    }

    if (typeof window.KustomerCore.addListener !== 'function') {
      sendErrorToSentry({
        consoleError: true,
        conversationId,
        error: 'setupListeners -- window.KustomerCore.addListener not defined',
      });
      return;
    }

    /**
     * call removeListeners here because setupListeners is called in both the original
     * useEffect as well as the debounce function
     */
    removeListeners();

    try {
      window.KustomerCore.addListener('onMessageReceived', onMessageReceived);
      window.KustomerCore.addListener(
        'onConversationCreate',
        onConversationCreate,
      );
      window.KustomerCore.addListener(
        'onConversationEnded',
        onConversationEnded,
      );
      window.KustomerCore.addListener(
        'onConversationUnended',
        onConversationUnended,
      );
      window.KustomerCore.addListener(
        'onAgentTypingActivity',
        onAgentTypingActivity,
      );
      window.KustomerCore.addListener('onAssistantEnded', onAssistantEnded);
      window.KustomerCore.addListener(
        'onSatisfactionReceived',
        onSatisfactionReceived,
      );
    } catch (err) {
      sendErrorToSentry({
        consoleError: true,
        conversationId,
        error: `setupListeners -- ${getExceptionMessage(err)}`,
      });
    }
  }, [
    conversationId,
    onAgentTypingActivity,
    onAssistantEnded,
    onConversationCreate,
    onConversationEnded,
    onConversationUnended,
    onMessageReceived,
    onSatisfactionReceived,
    removeListeners,
  ]);

  /**
   * Debounce the setupListenersAndInitKustomerSDK to prevent multiple calls
   */
  const setupListenersAndInitKustomerSDK = React.useCallback(
    async ({
      forethoughtConversationId,
      integrationFields,
    }: {
      forethoughtConversationId: string;
      integrationFields: KustomerLiveChatIntegrationConfig['integration_fields'];
    }) => {
      let kustomerSettings: KustomerSettings;

      try {
        kustomerSettings = await retryInitializeKustomerSdk({
          brandId: integrationFields.brand_id,
          email: integrationFields.visitor_email,
          forceInit: !chatId,
          jwt: onLoadJwt ?? integrationFields.jwt,
          kustomerConversationId: chatId,
          onLoginFailure: err => {
            dispatch(
              sendTrackingEvent({
                error: `setupListenersAndInitKustomerSDK -- ${err}`,
                event_type: 'widget-handoff-auth-failure',
                integration: 'kustomer-live-chat',
              }),
            );
          },
        });
      } catch (err) {
        handleLiveChatFailure({
          error: `Failed to initialize Kustomer SDK -- ${getExceptionMessage(err)}`,
          integration: 'kustomer-live-chat',
        });
        return;
      }

      // add listeners before creating conversation
      setupListeners();

      /**
       * Create conversation if it doesn't exist
       *
       * ORDER MATTERS
       */
      if (!chatId) {
        /**
         * try to update customer attributes but it will fail
         * if no tracking token is set
         */
        if (integrationFields.visitor_email) {
          try {
            await updateKustomerCustomer(integrationFields.visitor_email);
          } catch {
            // noop
          }
        }

        /**
         * conversation needs to be created before customer info is updated
         * because Kustomer sets a tracking token after this call
         */
        let kustomerConversation: KustomerConversationResponse;
        try {
          kustomerConversation = await createKustomerConversation({
            integrationFields,
          });
        } catch (err) {
          handleLiveChatFailure({
            error: `Failed to create Kustomer conversation -- ${getExceptionMessage(err)}`,
            integration: 'kustomer-live-chat',
          });
          sendErrorToSentry({
            additionalContext: { integrationFields },
            consoleError: true,
            conversationId: forethoughtConversationId,
            error: `Failed to create Kustomer conversation -- ${getExceptionMessage(err)}`,
          });
          return;
        }

        // update customer attributes
        if (integrationFields.visitor_email) {
          await updateKustomerCustomer(integrationFields.visitor_email);
        }

        const message =
          integrationFields.visitor_question ||
          'See transcript note for user interactions';
        let assistantPayload: KustomerInitAssistantResponse | undefined;

        try {
          assistantPayload = await applyKustomerCoreFunctionWithCallback(
            'initAssistant',
            {
              assistant: kustomerSettings.activeAssistant,
            },
          );
          dispatch(setIsInAssistantMode(true));
        } catch (err) {
          sendErrorToSentry({
            additionalContext: {
              assistant: kustomerSettings.activeAssistant,
              chatId: kustomerConversation.conversationId,
              message,
            },
            consoleError: true,
            conversationId: forethoughtConversationId,
            error: `Failed to initialize assistant -- ${getExceptionMessage(
              err,
            )}`,
          });
        }

        if (assistantPayload && kustomerSettings.activeAssistant) {
          try {
            await sendKustomerMessage({
              body: message,
              conversationId: kustomerConversation.conversationId,
              initAssistantPayload: assistantPayload,
              isInAssistantMode: true,
            });
          } catch (err) {
            sendErrorToSentry({
              additionalContext: {
                assistant: kustomerSettings.activeAssistant,
                assistantPayload: assistantPayload,
                chatId: kustomerConversation.conversationId,
                message,
              },
              consoleError: true,
              conversationId: forethoughtConversationId,
              error: `Failed to send initial message to assistant -- ${err}`,
            });
          }
        } else {
          try {
            await sendKustomerMessage({
              body: message,
              conversationId: kustomerConversation.conversationId,
            });
          } catch (err) {
            sendErrorToSentry({
              additionalContext: {
                chatId: kustomerConversation.conversationId,
                message,
              },
              consoleError: true,
              conversationId: forethoughtConversationId,
              error: `Failed to send initial message -- ${err}`,
            });
          }
        }

        // post transcript note
        dispatch(
          updateHelpdeskConversation({
            forethought_conversation_id: forethoughtConversationId,
            helpdesk_conversation_id: kustomerConversation.conversationId,
            include_attachments: true,
            include_transcript: true,
            integration: 'kustomer',
          }),
        );

        // send tracking event
        dispatch(
          sendTrackingEvent({
            conversation_id: forethoughtConversationId,
            event_type: 'widget-customer-handoff',
            helpdesk_conversation_id: kustomerConversation.conversationId,
            integration: 'kustomer-live-chat',
            success: true,
          }),
        );
      }

      if (chatId) {
        try {
          /**
           * Try and get any messages that were missed
           */
          const messages = await getMessages(chatId);
          messages.messages.forEach(message => {
            onMessageReceived(message);
          });
        } catch (err) {
          sendErrorToSentry({
            consoleError: true,
            conversationId: forethoughtConversationId,
            error: `Failed to get messages -- ${getExceptionMessage(err)}`,
          });
        }
      }
    },
    [
      chatId,
      dispatch,
      handleLiveChatFailure,
      onLoadJwt,
      onMessageReceived,
      setupListeners,
    ],
  );

  /**
   * Debounce the script initialization to prevent multiple scripts from being added
   */
  const initKustomerChatScript = React.useCallback(
    async ({
      forethoughtConversationId,
      integrationFields,
    }: {
      forethoughtConversationId: string;
      integrationFields: KustomerLiveChatIntegrationConfig['integration_fields'];
    }) => {
      try {
        await addKustomerSdkScript({
          apiKey: integrationFields.api_key,
          onLoad: async () => {
            setupListenersAndInitKustomerSDK({
              forethoughtConversationId,
              integrationFields,
            });
          },
        });
      } catch (err) {
        handleLiveChatFailure({
          error: `initKustomerChatScript -- ${err}`,
          integration: 'kustomer-live-chat',
        });
      }
    },
    [handleLiveChatFailure, setupListenersAndInitKustomerSDK],
  );

  React.useEffect(() => {
    /**
     * we use a timeout because of quick changes to
     * isCurrentTabPrimary / liveChatWidgetComponent.isSent, and
     * the async Kustomer Core SDK setup and initialization.
     */
    let timeout: NodeJS.Timeout | undefined;

    if (
      isCurrentTabPrimary &&
      liveChatWidgetComponent &&
      liveChatWidgetComponent.component_fields.integration_config
        .integration === 'kustomer'
    ) {
      const integrationFields =
        liveChatWidgetComponent.component_fields.integration_config
          .integration_fields;

      if (window.KustomerCore) {
        /**
         * because of the timeout we want to call setupListeners here
         * for page refreshes / new tabs during ongoing conversation.
         */
        setupListeners();

        timeout = setTimeout(() => {
          setupListenersAndInitKustomerSDK({
            forethoughtConversationId: conversationId,
            integrationFields,
          });
        }, TIMEOUT_DURATION);
      } else {
        timeout = setTimeout(() => {
          initKustomerChatScript({
            forethoughtConversationId: conversationId,
            integrationFields,
          });
        }, TIMEOUT_DURATION);
      }
    }

    return () => {
      try {
        clearTimeout(timeout);
        removeListeners();
      } catch {
        // do nothing
      }
    };
  }, [
    conversationId,
    initKustomerChatScript,
    isCurrentTabPrimary,
    liveChatWidgetComponent,
    removeListeners,
    setupListeners,
    setupListenersAndInitKustomerSDK,
  ]);
};
